import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Select,
  Spin,
  message,
} from 'antd';
import type {
  DataTypeKey,
} from '@/features/dashboard/types/dashboard.types';
import { useResponsive } from '@/shared/hooks/useResponsive';
import { logger } from '@/shared/lib/logger';
import { DataTypeSelector } from '@/features/dashboard/components/DataTypeSelector';
import { RealtimeChart } from '@/features/chart/components/RealtimeChart';
import { getAllPanels } from '@/features/panels/services/panelApiService';
import type { PanelBasicInfo } from '@/features/panels/services/panelApiService';

const REALTIME_DATA_CONSTANTS = {
  GRID_GUTTER: [16, 16] as [number, number],
  CHART_HEIGHT: {
    mobile: 400,
    desktop: 600,
  },
  UPDATE_INTERVAL_MS: 60000,
  MAX_DATA_POINTS: 100,
  DECIMAL_BASE: 10,
} as const;

export const RealtimeDataPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialPanelIdParam = searchParams.get('panelId');
  const { isMobile } = useResponsive();

  const [panels, setPanels] = useState<readonly PanelBasicInfo[]>([]);
  const [loadingPanels, setLoadingPanels] = useState<boolean>(true);
  // UI層ではプリミティブ値で状態管理（サービス呼び出し時にサービス内部でドメインオブジェクトに変換）
  const [selectedPanelId, setSelectedPanelId] = useState<number | null>(
    initialPanelIdParam !== null ? parseInt(initialPanelIdParam, REALTIME_DATA_CONSTANTS.DECIMAL_BASE) : null
  );
  const [selectedDataTypes, setSelectedDataTypes] = useState<DataTypeKey[]>(['l_af_supply_volume', 'l_h_ratio']);

  // 盤リストを取得
  useEffect(() => {
    const fetchPanels = async (): Promise<void> => {
      try {
        setLoadingPanels(true);
        const panelsData = await getAllPanels();
        setPanels(panelsData);

        // 初回表示時、選択されていない場合は最初の盤を選択
        if (selectedPanelId === null && panelsData.length > 0) {
          setSelectedPanelId(panelsData[0].id);
        }
      } catch (error) {
        logger.captureError(error, 'RealtimeDataPage.fetchPanels');
        message.error('盤情報の取得に失敗しました');
      } finally {
        setLoadingPanels(false);
      }
    };

    fetchPanels();
  }, [selectedPanelId]);

  const handlePanelChange = (panelId: number): void => {
    setSelectedPanelId(panelId);
  };

  const handleDataTypesChange = (dataTypes: DataTypeKey[]): void => {
    setSelectedDataTypes(dataTypes);
  };

  // 盤オプションを生成
  const panelOptions = panels.map((panel) => ({
    value: panel.id,
    label: panel.panelName,
  }));

  if (loadingPanels) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" tip="盤情報を読み込み中..." />
      </div>
    );
  }

  return (
    <div>
      <div className={`mb-6 ${isMobile ? 'flex flex-col gap-4' : 'flex flex-row gap-4'}`}>
        <Select
          className={isMobile ? "w-full" : "w-[200px]"}
          value={selectedPanelId ?? undefined}
          onChange={handlePanelChange}
          options={panelOptions}
          placeholder="対象盤選択"
        />
        <DataTypeSelector
          value={selectedDataTypes}
          onChange={handleDataTypesChange}
          isMobile={isMobile}
        />
      </div>

      {selectedPanelId !== null && (
        <RealtimeChart
          panelId={selectedPanelId}
          selectedDataTypes={selectedDataTypes}
          updateIntervalMs={REALTIME_DATA_CONSTANTS.UPDATE_INTERVAL_MS}
        />
      )}
    </div>
  );
};
